import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Home, BookOpen, LayoutGrid, Star, Mail, Image, Gem,
  Lock, Settings, Download, MessageSquare, Share2, Bell, Clock, Shield,
  User, Users, HelpCircle, LogOut, ChevronRight, ChevronDown, Target, ClipboardList
} from 'lucide-react';
import SecureLS from 'secure-ls';
import { logout, axiosInstance } from '../../services/api';
import { clearUserData, getUserFirstName } from '../../utils/userUtils';
import { getUser, removeUser, removeFamilies } from '../../utils/secureStorage';
import { ReactComponent as Logo } from '../../assets/logo-1g2n.svg';
import FamilySwitcher from './FamilySwitcher';
import NotificationBell from './NotificationBell';
import '../../styles/hub-chapters.css';

/**
 * HubLayout Component
 *
 * Layout wrapper for Hub and My Life pages with left navigation sidebar
 * and top navbar. Based on hub-with-chapters.html design.
 */
function HubLayout({ children, activeView = 'hub', sidebarExtra = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const ls = new SecureLS({ encodingType: 'aes' });

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userName, setUserName] = useState('User');
  const [userInitial, setUserInitial] = useState('U');
  const [userPhotoUrl, setUserPhotoUrl] = useState(null);

  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
    setIsLoggedIn(loggedIn);
    if (loggedIn) {
      const userData = getUser();
      setUser(userData);
      if (userData && userData.first_name) {
        setUserName(userData.first_name);
        setUserInitial(userData.first_name.charAt(0).toUpperCase());
      }
    }

    // Fetch profile photo
    if (loggedIn) {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      axiosInstance.get('/account/api/accounts/profile/')
        .then(res => {
          const photoUrl = res.data?.profile?.profile_photo_url;
          if (photoUrl) setUserPhotoUrl(photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`);
        })
        .catch(() => {});
    }

    // Fallback to getUserFirstName
    const name = getUserFirstName();
    if (name && name !== 'there') {
      setUserName(name);
      setUserInitial(name.charAt(0).toUpperCase());
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
    setIsUserDropdownOpen(false);
  };

  const toggleUserDropdown = (e) => {
    e.stopPropagation();
    setIsUserDropdownOpen(!isUserDropdownOpen);
    setIsDropdownOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('isLoggedIn');
      removeUser();
      removeFamilies();
      localStorage.removeItem('familyId');
      localStorage.removeItem('primaryFamilyId');
      ls.remove('authToken');
      clearUserData();
      setIsLoggedIn(false);
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const isActiveNav = (navItem) => {
    switch (navItem) {
      case 'Board':
        return location.pathname === '/board' || location.pathname === '/dashboard' || location.pathname === '/stories';
      case 'My Life':
        return location.pathname === '/my-life-v2' ||
               location.pathname === '/my-life' ||
               location.pathname === '/milestones' ||
               location.pathname === '/memories';
      case 'Family':
        return location.pathname === '/family-tree' || location.pathname === '/family-hub';
      case 'Time Capsules':
        return location.pathname === '/timecapsule';
      case 'Voice':
        return location.pathname === '/tell-a-story';
      case 'Settings':
        return location.pathname === '/settings';
      default:
        return false;
    }
  };

  const handleNavigation = (navItem) => {
    setIsMobileMenuOpen(false);
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    switch (navItem) {
      case 'Board':
        navigate('/board');
        break;
      case 'My Life':
        navigate('/my-life');
        break;
      case 'Family':
        navigate('/family-hub');
        break;
      case 'Time Capsules':
        navigate('/timecapsule');
        break;
      case 'Voice':
        navigate('/tell-a-story');
        break;
      case 'Settings':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  // Determine active left nav item based on current path
  const getActiveLeftNav = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/board') return 'hub';
    if (path.startsWith('/my-life')) return 'life';
    if (path.includes('milestone')) return 'milestones';
    if (path.startsWith('/timecapsule')) return 'capsules';
    if (path.startsWith('/photos')) return 'photos';
    if (path.startsWith('/wisdom')) return 'wisdom';
    if (path.startsWith('/family-tree')) return 'tree';
    if (path.startsWith('/family-hub')) return 'members';
    if (path === '/settings') return 'settings';
    return activeView;
  };

  const currentLeftNav = getActiveLeftNav();
  const isSettingsPage = location.pathname === '/settings';

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="hub-layout-wrapper" style={{ maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Top Navigation Bar */}
      <header className="bg-[#556B2F] text-white px-2 sm:px-8 py-2 sm:py-3 flex justify-between items-center shadow-md sticky top-0 z-[200] gap-1 sm:gap-2">
        <div className="flex items-center">
          <button
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
            className="focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#556B2F] rounded"
          >
            <Logo className="h-7 sm:h-10 w-auto cursor-pointer" style={{ fill: 'white' }} />
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="lg:hidden p-2 rounded-md hover:bg-[#4A5D23] transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block">
          <ul className="flex gap-4 xl:gap-8 list-none">
            {['Board', 'My Life', 'Family', 'Time Capsules'].map((item) => (
              <li key={item}>
                <button
                  onClick={() => handleNavigation(item)}
                  className={`text-white no-underline px-3 xl:px-4 py-2 rounded-md transition-colors hover:bg-[#4A5D23] text-sm xl:text-base ${
                    isActiveNav(item) ? 'bg-[#4A5D23]' : ''
                  }`}
                >
                  {item}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#556B2F] lg:hidden z-50">
            <nav className="px-4 py-4">
              <ul className="space-y-2">
                {['Board', 'My Life', 'Family', 'Time Capsules'].map((item) => (
                  <li key={item}>
                    <button
                      onClick={() => handleNavigation(item)}
                      className={`w-full text-left text-white px-4 py-3 rounded-md hover:bg-[#4A5D23] transition-colors ${
                        isActiveNav(item) ? 'bg-[#4A5D23]' : ''
                      }`}
                    >
                      {item}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink">
          {/* Notification Bell */}
          {isLoggedIn && <NotificationBell />}

          {/* Family Switcher - shown for multi-family users */}
          {isLoggedIn && <FamilySwitcher />}

          {/* User Profile */}
          {isLoggedIn && user ? (
            <div className="relative" ref={userDropdownRef}>
              <button
                onClick={toggleUserDropdown}
                className="flex items-center gap-2 p-2 rounded-full hover:bg-[#4A5D23] transition-colors"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#6B8E23] flex items-center justify-center font-bold text-sm sm:text-base overflow-hidden">
                  {userPhotoUrl ? (
                    <img src={userPhotoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    user.first_name ? user.first_name.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
                <span className="hidden sm:block text-sm lg:text-base">{user.first_name || 'User'}</span>
                <ChevronDown size={14} style={{ opacity: 0.5 }} />
              </button>
              {isUserDropdownOpen && (
                <div className="da-dropdown">
                  <div className="da-header">
                    <div className="da-avatar" style={{ overflow: 'hidden' }}>
                      {userPhotoUrl ? (
                        <img src={userPhotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      ) : (
                        <>
                          {user.first_name ? user.first_name.charAt(0).toUpperCase() : 'U'}
                          {user.last_name ? user.last_name.charAt(0).toUpperCase() : ''}
                        </>
                      )}
                    </div>
                    <div className="da-info">
                      <div className="da-name">{user.first_name} {user.last_name}</div>
                      <div className="da-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="da-body">
                    <Link to="/settings" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                      <div className="da-link-icon"><Settings size={16} /></div>
                      <div className="da-link-label">Settings</div>
                      <ChevronRight size={16} className="da-link-chevron" />
                    </Link>
                    <Link to="/family-hub" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                      <div className="da-link-icon"><Users size={16} /></div>
                      <div className="da-link-label">
                        My Family
                        <div className="da-link-sub">Family members connected</div>
                      </div>
                    </Link>
                    <Link to="/milestone-registry" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                      <div className="da-link-icon"><Target size={16} /></div>
                      <div className="da-link-label">Milestone Registry</div>
                    </Link>
                    <Link to="/family-manager" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                      <div className="da-link-icon"><ClipboardList size={16} /></div>
                      <div className="da-link-label">Family Manager</div>
                    </Link>
                    <Link to="/help" className="da-link" onClick={() => setIsUserDropdownOpen(false)}>
                      <div className="da-link-icon"><HelpCircle size={16} /></div>
                      <div className="da-link-label">Help & Support</div>
                    </Link>
                    <div className="da-divider"></div>
                    <button onClick={handleLogout} className="da-link da-link-danger">
                      <div className="da-link-icon"><LogOut size={16} /></div>
                      <div className="da-link-label">Log Out</div>
                    </button>
                  </div>
                  <div className="da-footer">
                    <div className="da-footer-text"><span>OneGeneration ToNext</span> · Family Legacy Platform</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={handleLogin}
              className="text-sm sm:text-base hover:text-[#E8F5E8] transition-colors"
            >
              Login
            </button>
          )}
        </div>
      </header>

      {/* Main Layout with Left Nav */}
      <div className="hub-layout">
        {/* Left Navigation */}
        <nav className="left-nav">
          {/* Main Section - hidden on Settings page */}
          {!isSettingsPage && (
            <>
              <div className="nav-section">
                <div className="nav-section-label">Main</div>
                <a
                  className={`nav-item ${currentLeftNav === 'hub' ? 'active' : ''}`}
                  onClick={() => navigate('/board')}
                >
                  <span className="nav-icon"><Home size={16} /></span>
                  My Hub
                </a>
                <a
                  className={`nav-item ${currentLeftNav === 'life' ? 'active' : ''}`}
                  onClick={() => navigate('/my-life')}
                >
                  <span className="nav-icon"><BookOpen size={16} /></span>
                  My Life Story
                </a>
              </div>

              <div className="nav-divider"></div>

              <div className="nav-section">
                <a
                  className={`nav-item ${currentLeftNav === 'milestones' ? 'active' : ''}`}
                  onClick={() => navigate('/my-life/add-milestone')}
                >
                  <span className="nav-icon"><Star size={16} /></span>
                  Milestones
                </a>
                <a
                  className={`nav-item ${currentLeftNav === 'capsules' ? 'active' : ''}`}
                  onClick={() => navigate('/timecapsule')}
                >
                  <span className="nav-icon"><Mail size={16} /></span>
                  Time Capsules
                </a>
                <a
                  className={`nav-item ${currentLeftNav === 'photos' ? 'active' : ''}`}
                  onClick={() => navigate('/photos')}
                >
                  <span className="nav-icon"><Image size={16} /></span>
                  Photos & Media
                </a>
                <a
                  className={`nav-item ${currentLeftNav === 'wisdom' ? 'active' : ''}`}
                  onClick={() => navigate('/wisdom')}
                >
                  <span className="nav-icon"><Gem size={16} /></span>
                  Family Wisdom
                </a>
              </div>

              <div className="nav-divider"></div>

              <div className="nav-section">
                <div className="nav-section-label">Quick Create</div>
                <a
                  className="nav-item"
                  onClick={() => navigate('/tell-a-story')}
                >
                  <span className="nav-icon"><BookOpen size={16} /></span>
                  New Story
                </a>
                <a
                  className="nav-item"
                  onClick={() => navigate('/timecapsule')}
                >
                  <span className="nav-icon"><Mail size={16} /></span>
                  New Capsule
                </a>
                <a
                  className="nav-item"
                  onClick={() => navigate('/my-life/add-milestone')}
                >
                  <span className="nav-icon"><Star size={16} /></span>
                  New Milestone
                </a>
                <a
                  className="nav-item"
                  onClick={() => navigate('/letter-creation')}
                >
                  <span className="nav-icon"><MessageSquare size={16} /></span>
                  New Letter
                </a>
              </div>

              {/* Page-specific sidebar content */}
              {sidebarExtra && (
                <>
                  <div className="nav-divider"></div>
                  {sidebarExtra}
                </>
              )}
            </>
          )}

          {/* Settings Section - only on Settings page */}
          {isSettingsPage && (
            <div className="nav-section">
              <div className="nav-section-label">Settings</div>
              <a
                className={`nav-item active`}
                onClick={() => navigate('/settings')}
              >
                <span className="nav-icon"><Settings size={16} /></span>
                Settings (show all)
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('personal-info')}>
                <User size={14} />
                Personal Info
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('my-families')}>
                <Home size={14} />
                My Families
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('privacy')}>
                <Lock size={14} />
                Privacy
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('interactions')}>
                <MessageSquare size={14} />
                Interactions
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('sharing')}>
                <Share2 size={14} />
                Sharing
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('notifications')}>
                <Bell size={14} />
                Notifications
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('time-capsules')}>
                <Clock size={14} />
                Time Capsules
              </a>
              <a className="nav-jump-link" onClick={() => scrollToSection('stewardship')}>
                <Shield size={14} />
                Stewardship
              </a>
            </div>
          )}
        </nav>

        {/* Main Content */}
        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
}

export default HubLayout;
